<template>
  <v-card class="ml-4 mt-2">
    <v-toolbar v-if="value" color="primary" flat dark dense>
      <v-toolbar-title class="ml-2">
        Customer Details [{{ value }}]
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <anonymization-modal
        :customer-id="value"
        :customer-name="customerDetail.business_name"
        @anonymization-complete="handleAnonymizationComplete"
      />
    </v-toolbar>
    <v-card-text v-if="value">
      <v-card flat>
        <v-card-text>
          <v-row align="center">
            <read-only-field label="Name" :value="customerDetail.business_name" :loading="loading"/>
            <read-only-field label="DOB" :value="customerDetail.birth_date" :loading="loading"/>
          </v-row>
        </v-card-text>
      </v-card>

      <v-container fluid class="ma-0 pa-0">
        <v-row no-gutters>
          <v-col cols="4">
            <v-card flat>
              <v-card-title>Emails</v-card-title>
              <v-card-text>
                <v-list dense class="v-list-scroll" max-height="150px">
                   <v-list-item
                    v-for="email in contactDetail.emails"
                    :key="email.email + contactDetail.customer_id"
                  >
                     <v-list-item-content>
                       <v-list-item-title>
                         {{email.email}}&nbsp;
                         <email-suppression-chip v-model="email.email">{{ email.order === 1 ? 'latest' : ''}}</email-suppression-chip>
                       </v-list-item-title>
                     </v-list-item-content>
                   </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="4">
            <v-card flat>
              <v-card-title>Phones</v-card-title>
              <v-card-text>
                <v-list dense class="v-list-scroll" max-height="150px">
                   <v-list-item
                    v-for="(phone, idx) in contactDetail.phones"
                    :key="phone.phone + contactDetail.customer_id"
                  >
                     <v-list-item-content>
                       <v-list-item-title>
                         {{phone.phone | phone}}
                         <phone-suppression-chip v-model="customerPhones[idx]" :customer-id="contactDetail.customer_id">
                          {{ phone.type }} {{ phone.order === 1 ? ' - latest' : ''}}
                         </phone-suppression-chip>
                      </v-list-item-title>
                     </v-list-item-content>
                   </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="4">
            <v-card flat>
              <v-card-title>Addresses</v-card-title>
              <v-card-text>
                <v-list dense class="v-list-scroll" max-height="150px">
                   <v-list-item
                    v-for="(address, idx) in customerAddresses"
                    :key="address.address_id + contactDetail.customer_id"
                  >
                     <v-list-item-content>
                       <v-list-item-title>
                         <span v-html="addressBreakLine(address.address)"></span>
                         &nbsp;
                         <address-suppression-chip v-model="customerAddresses[idx]" :customer-id="contactDetail.customer_id">
                          {{ address.order === 1 ? 'latest' : ''}}
                         </address-suppression-chip>
                       </v-list-item-title>
                     </v-list-item-content>
                   </v-list-item>
                </v-list>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-tabs v-model="tabIndex" grow class="mt-6">
        <v-tab>
          <v-icon left>mdi-car-info</v-icon>
          Vehicles and Transactions
        </v-tab>
        <v-tab>
          <v-icon left>mdi-email-send</v-icon>
          Campaigns and Email Events
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tabIndex">
        <!-- Vehicle & TXN Tab -->
        <v-tab-item>
          <template v-if="tabIndex === 0">
            <v-card flat>
              <v-card-title>Vehicles</v-card-title>
              <v-card-text>
                <v-data-table
                  v-model="selectedTransaction"
                  :headers="vehicleColumns"
                  :items="vehicles"
                  item-key="vehicle_vin"
                  :loading="loading"
                  :single-select="singleSelect"
                  show-select
                >
                  <template v-slot:item.vehicle_vin="{ item }">
                    {{ item.vehicle_vin }}
                    <vin-suppression-chip :value="item" :customer-id="item.customer_id">
                    </vin-suppression-chip>
                  </template>
                  <!--
                  <template v-slot:item.vehicle_traded_on="{ item }">
                    {{ item.vehicle_vin }}
                    <vin-suppression-chip :value="item" :customer-id="item.customer_id">
                    </vin-suppression-chip>
                  </template>
                  -->
                </v-data-table>
              </v-card-text>
            </v-card>
            <v-card flat>
              <v-card-title>Transactions {{ selectedVin ? `[${selectedVin}]` : '' }}</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="transactionColumns"
                  :items="transactions"
                  item-key="transaction_id"
                  :loading="loading"
                >
                  <template v-slot:item.transaction_type="{ item }">
                    <v-chip :color="txnTypeColor(item.transaction_type)">{{item.transaction_type}}</v-chip>
                  </template>
                  <template v-slot:item.transaction_revenue="{ item }">
                    {{ item.transaction_revenue | toCurrency }}
                  </template>
                  <template v-slot:item.age_in_words="{ item }">
                    <v-chip>{{ item.age_in_words }}</v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </template>
        </v-tab-item>

        <!-- Campaign & Email Tab -->
        <v-tab-item>
          <template v-if="tabIndex === 1">
            <customer-mail-events v-model="customerEmails" :loading="loading"></customer-mail-events>
          </template>
        </v-tab-item>
      </v-tabs-items>

    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { OmniSearchApi } from '@/services'
import { pathOr, find, propEq, always, filter } from 'ramda'
import { DateTime } from 'luxon'
import { ageInWords } from '@/utils'

import ReadOnlyField from '@/components/ui/ReadOnlyField'
import CustomerMailEvents from '@/components/mail_events/CustomerMailEvents'
import EmailSuppressionChip from '@/components/mail_events/EmailSuppressionChip'
import AddressAppendsChip from '@/components/customer_appends/AddressAppendsChip'
import VinAppendsChip from '@/components/customer_appends/VinAppendsChip'
import PhoneAppendsChip from '@/components/customer_appends/PhoneAppendsChip'
import AnonymizationModal from '@/components/customer_anonymization/AnonymizationModal'

export default {
  name: 'omni-search-show',
  props: {
    value: {
      type: [String, Number]
    }
  },
  components: {
    [ReadOnlyField.name]: ReadOnlyField,
    [CustomerMailEvents.name]: CustomerMailEvents,
    [EmailSuppressionChip.name]: EmailSuppressionChip,
    [AddressAppendsChip.name]: AddressAppendsChip,
    [VinAppendsChip.name]: VinAppendsChip,
    [PhoneAppendsChip.name]: PhoneAppendsChip,
    [AnonymizationModal.name]: AnonymizationModal
  },
  data: function () {
    return {
      loading: false,
      tabIndex: 0,
      singleSelect: true,
      contactDetail: {},
      txnDetail: [],
      vehicleColumns: [
        { text: 'YMM', value: 'vehicle_ymm' },
        { text: 'VIN', value: 'vehicle_vin' },
        { text: 'Sold', value: 'vehicle_traded_on' },
        { text: 'Last Mileage', value: 'vehicle_last_odometer' }
      ],
      selectedTransaction: [],
      transactionColumns: [
        { text: 'Type', value: 'transaction_type' },
        { text: 'Date', value: 'transaction_date' },
        { text: 'YMM', value: 'vehicle_ymm' },
        { text: 'VIN', value: 'vehicle_vin' },
        { text: 'RO#', value: 'transaction_ro_number' },
        { text: 'Revenue', value: 'transaction_revenue' },
        { text: 'Age', value: 'age_in_words' },
        { text: 'Odometer', value: 'transaction_odometer' }
      ]
    }
  },
  computed: {
    ...mapState('DealerStore', { dealer: 'dealer' }),
    ...mapState('CustomerAppendsStore', { customerAppends: 'customerAppends' }),

    // account_id:"luther"
    // birth_date:"1984-01-21"
    // business_name:"DELTA BRYANT"
    // customer_id:1287130
    // dealer_id:"140874"
    // is_business:false
    // transaction_date:"2010-01-08"
    // transaction_id:9406978
    // transaction_odometer:44255
    // transaction_revenue:"12350.00"
    // transaction_ro_number:"101912"
    // transaction_type:"sale"
    // vehicle_last_odometer:44255
    // vehicle_trade_on:null
    // vehicle_vin:"2G1WW12E449152696"
    // vehicle_ymm:"2004 CHEVROLET Monte Carlo"
    customerEmails: function () {
      return (this.contactDetail.emails || []).map(detail => detail.email)
    },
    customerAddresses: function () {
      return (this.contactDetail.addresses || [])
    },
    customerVins: function () {
      return (this.contactDetail.vins || [])
    },
    customerPhones: function () {
      return (this.contactDetail.phones || [])
    },
    customerDetail: function () {
      return pathOr({}, [0], this.txnDetail)
    },
    vehicles: function () {
      const vins = new Set(this.txnDetail.map(detail => detail.vehicle_vin))
      return Array.from(vins).map(vin => find(propEq('vehicle_vin', vin))(this.txnDetail))
    },
    selectedVin: function () {
      return (this.selectedTransaction[0] || {}).vehicle_vin
    },
    transactions: function () {
      let txnFilter = always(true)
      if (this.selectedVin) {
        txnFilter = propEq('vehicle_vin', this.selectedVin)
      }
      return (filter(txnFilter, this.txnDetail)).map((detail) => {
        return {
          ...detail,
          ...{
            transaction_date: DateTime.fromISO(detail.transaction_date).toLocaleString(DateTime.DATE_SHORT),
            age_in_words: ageInWords(detail.transaction_date)
          }
        }
      })
    }
  },
  watch: {
    value (newVal) {
      this.tabIndex = 0
      this.contactDetail = {}
      this.txnDetail = []
      this.selectedTransaction = []
      if (newVal) {
        this.loadCustomerDetails(newVal)
      }
    }
  },
  methods: {
    handleAnonymizationComplete() {
      // Show success message
      this.$flash({
        showClose: true,
        message: 'Customer anonymization request has been submitted successfully. The process will complete in the background.',
        type: 'success',
        duration: 5000
      })
      
      // Optionally reload customer details to show anonymized state
      // Or clear the current customer selection
      // this.loadCustomerDetails(this.value)
    },
    txnTypeColor (txnDetail) {
      if (txnDetail === 'sale') {
        return 'teal'
      } else {
        return 'cyan'
      }
    },
    loadCustomerDetails (customerId) {
      this.loading = true
      const contactP = OmniSearchApi.show(this.dealer.id, customerId, 'contact')
      const txnP = OmniSearchApi.show(this.dealer.id, customerId, 'transaction')
      const appendsP = this.$store.dispatch('CustomerAppendsStore/getCustomerAppends', { customer_id: customerId })
      Promise.all([contactP, txnP, appendsP])
        .then(results => {
          this.contactDetail = results[0].data
          this.txnDetail = results[1].data
          this.loading = false
        })
        .catch(error => {
          this.$logger.error('Unable to load customer details', error.message)
          this.loading = false
          this.$flash({
            showClose: true,
            message: 'Unable to load customer details. ' + error.message,
            type: 'error',
            duration: 0
          })
        })
    },
    addressBreakLine (address) {
      if (address) {
        const aParts = address.split(',')
        return `${aParts[0]}<br/>${aParts.slice(1).join(',')}`
      } else {
        return '&nbsp;'
      }
    }
  }
}
</script>

<style>
</style>
